import { useQuery } from "@apollo/client/react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocation, useParams } from "react-router";
import { BackButton } from "@/components/common/BackButton";
import { Button } from "@/components/design/Button";
import { ShareDetailStatus } from "@/components/share/ShareDetailStatus";
import { loginUrlFor } from "@/auth/login-url";
import { publicClient } from "@/graphql/public-client";
import { PublicListingSearchTarget, PublicOpenListingDocument } from "@/graphql/generated/public";
import { getConfig } from "@/lib/config";
import { ListingPostLayout } from "@/pages/share/listings/ListingPostView";
import { PublicApplyDialog } from "./PublicApplyDialog";

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// SHARE: eine offene Ausschreibung ohne Konto, unter derselben URL und in derselben Darstellung wie mit Konto
// (ListingPostLayout, wie OpenListingPage), gelesen nur aus dem öffentlichen Schema. Bewerben geht ohne Konto; die
// Anmeldung wird angeboten, nicht verlangt. Unbekannte und nicht öffentliche Ausschreibungen sehen gleich aus.
export function PublicOpenListingPage() {
  const intl = useIntl();
  const location = useLocation();
  const { listingId, id } = useParams();
  const targetId = listingId ?? id ?? "";
  const valid = UUID.test(targetId);
  const query = useQuery(PublicOpenListingDocument, { client: publicClient, variables: { id: targetId }, skip: !valid });
  const listing = valid ? query.data?.publicListing ?? null : null;
  const [applying, setApplying] = useState(false);
  const next = `${getConfig().basePath.replace(/\/$/, "")}${location.pathname}${location.search}`;

  return (
    <main className="w-full max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10" data-graphql="PublicOpenListing">
      <div className="mb-5">
        <BackButton />
      </div>
      {(query.loading && !listing) || query.error || !listing ? (
        <ShareDetailStatus
          loading={query.loading && !listing}
          error={query.error}
          notFoundLabel={intl.formatMessage({
            id: "share.public.listingUnavailable",
            defaultMessage: "Diese Ausschreibung ist nicht öffentlich oder nicht mehr offen",
          })}
        />
      ) : (
        <ListingPostLayout
          canManage={false}
          listing={{
            id: listing.id,
            reference: listing.reference,
            name: listing.name,
            description: listing.description,
            deadline: listing.deadline,
            publishedOn: listing.publishedOn,
            // The post shows no categories; the public schema names them in one language only.
            categories: [],
            mainImage: listing.mainImage,
            images: listing.images,
          }}
        >
          <Button id="public-open-listing-apply" className="mt-5" onClick={() => setApplying(true)}>
            {listing.searchTarget === PublicListingSearchTarget.Companies ? (
              <FormattedMessage id="share.public.apply.asCompany" defaultMessage="Als Unternehmen bewerben" />
            ) : (
              <FormattedMessage id="share.openListings.apply" defaultMessage="Bewerben" />
            )}
          </Button>
          <p className="smycles-field-meta mt-3 text-ink-muted">
            <FormattedMessage
              id="share.public.apply.loginHint"
              defaultMessage="Ohne Konto möglich. Du hast schon eines? {login}"
              values={{
                login: (
                  <a id="public-open-listing-login" href={loginUrlFor(next)} className="underline underline-offset-2 hover:text-ink">
                    <FormattedMessage id="share.public.login" defaultMessage="Anmelden" />
                  </a>
                ),
              }}
            />
          </p>
        </ListingPostLayout>
      )}
      {applying && listing ? (
        <PublicApplyDialog
          listingId={listing.id}
          listingName={listing.name ?? ""}
          company={listing.searchTarget === PublicListingSearchTarget.Companies}
          loginHref={loginUrlFor(next)}
          onClose={() => setApplying(false)}
        />
      ) : null}
    </main>
  );
}
